import { containsV1inPath, envFlags, FLAGS, getQueryParamsAndQueryString, } from '@thoughtspot/env-flags';
import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { collectEvent, UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useBlinkContextForGlobalNav } from '../../contexts/global-nav-blink-context';
import { useGetUserAccessibleOrgsByParamsQuery, useSwitchOrgMutation, } from '../../services/orgs/orgs-service';
import { accessibleOrgParams, OrgMenu } from './org-menu';
const IS_ORG_SWITCHED_AND_PAGE_REFRESHED = 'isOrgSwitchedAndPageRefreshed';
export const OrgMenuContainer = ({ isLegacy, isEmbedded = false, invalidateSessionInfoCallCache, }) => {
    const { t } = useTranslation();
    const { sessionService, navService, metricsService, } = useBlinkContextForGlobalNav();
    const { getCurrentOrgId, isReactshellEnabled } = sessionService;
    const [message, setMessage] = useState('');
    const [selectedOrgId, setSelectedOrgId] = useState('');
    const [numActiveOrgs, setNumActiveOrgs] = useState(0);
    const [isToastAlertVisible, setIsToastAlertVisible] = useState(false);
    const [areOrgsUpdated, setAreOrgsUpdated] = useState(false);
    const toastTimeoutRef = useRef(null);
    const areProfileAndHelpHidden = sessionService.areProfileAndHelpHidden();
    const { data: orgsList, refetch } = useGetUserAccessibleOrgsByParamsQuery({
        ...accessibleOrgParams,
    });
    const logMixPanelEvent = (userActionType) => {
        const selectedOrgObject = orgsList?.Data?.orgs?.filter((org) => org.orgId === selectedOrgId);
        if (!selectedOrgObject || selectedOrgObject.length === 0) {
            return;
        }
        const userAction = new UserAction(userActionType);
        collectEvent(userAction, {
            timestamp: Date.now(),
            toOrgId: selectedOrgObject[0].orgId,
            toOrgName: selectedOrgObject[0].orgName,
        });
    };
    const goToHomeAfterOrgSwitch = () => {
        const homePath = '/home';
        const { queryParams: originalParams } = getQueryParamsAndQueryString();
        const { [FLAGS.overrideOrgId.name]: deletedProp, ...queryParams } = originalParams;
        const orgQueryParamPresent = deletedProp !== undefined;
        let queryString = Object.keys(queryParams)
            .map(key => {
            if (queryParams[key]) {
                return `${key}=${queryParams[key]}`;
            }
            return '';
        })
            .filter(part => part)
            .join('&');
        if (queryString.length)
            queryString = `?${queryString}`;
        setTimeout(() => {
            if (!orgQueryParamPresent &&
                navService.getCurrentPathByLocationHash(false) === homePath) {
                window.location.reload();
            }
            else if (isReactshellEnabled() && containsV1inPath()) {
                navService.goToPath(`/${queryString}#${homePath}`, {
                    forceFullPageLoad: true,
                });
            }
            else {
                navService.goToPath(`${window.location.pathname}${queryString}#${homePath}`, {
                    forceFullPageLoad: true,
                });
                if (!orgQueryParamPresent) {
                    setTimeout(() => {
                        window.location.reload();
                    }, 100);
                }
            }
        }, 0);
    };
    const onSuccessSwitchOrg = (isSwitched) => {
        if (isSwitched) {
            invalidateSessionInfoCallCache();
            localStorage.setItem(IS_ORG_SWITCHED_AND_PAGE_REFRESHED, 'true');
            logMixPanelEvent(userActionTypeEnum.SWITCH_ORG);
            goToHomeAfterOrgSwitch();
        }
    };
    const [switchOrg, switchOrgObj] = useSwitchOrgMutation(onSuccessSwitchOrg, err => {
        logMixPanelEvent(userActionTypeEnum.SWITCH_ORG_FAILURE);
    }, selectedOrgId);
    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data?.messageType === 'orgsListUpdated') {
                setAreOrgsUpdated(true);
                refetch();
            }
        };
        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);
    useEffect(() => {
        if (orgsList) {
            const orgsListData = orgsList?.Data?.orgs?.filter((org) => org.isActive);
            if (orgsListData && Array.isArray(orgsListData)) {
                setNumActiveOrgs(previousValue => previousValue === 0 || areOrgsUpdated
                    ? orgsListData.length
                    : previousValue);
            }
            setAreOrgsUpdated(false);
            const isPageRefreshed = localStorage.getItem(IS_ORG_SWITCHED_AND_PAGE_REFRESHED);
            if (isPageRefreshed) {
                let currentOrgId = getCurrentOrgId();
                if (sessionService.isOrgPerUrlEnabled()) {
                    const overrideOrgId = envFlags.getValue(FLAGS.overrideOrgId.name);
                    if (overrideOrgId >= 0) {
                        currentOrgId = overrideOrgId;
                    }
                }
                const selectedOrgObject = orgsList?.Data?.orgs?.filter((org) => org.orgId === _.toString(currentOrgId));
                if (selectedOrgObject && selectedOrgObject.length > 0) {
                    localStorage.removeItem(IS_ORG_SWITCHED_AND_PAGE_REFRESHED);
                    setMessage(selectedOrgObject[0].orgName);
                    if (toastTimeoutRef.current) {
                        clearTimeout(toastTimeoutRef.current);
                    }
                    toastTimeoutRef.current = setTimeout(() => {
                        setIsToastAlertVisible(true);
                    }, 1000);
                }
            }
        }
    }, [selectedOrgId, orgsList]);
    useEffect(() => {
        if (selectedOrgId) {
            switchOrg();
        }
    }, [selectedOrgId]);
    return (React.createElement(React.Fragment, null,
        !sessionService.isOrgSwitcherHidden() &&
            (!sessionService.showOrgSwitcherToUsersWithMultipleOrgs() ||
                numActiveOrgs > 1) && (React.createElement(OrgMenu, { getCurrentOrgId: getCurrentOrgId, orgsListData: orgsList?.Data?.orgs?.filter((org) => org.isActive &&
                (!sessionService.isEnableSelectiveUserAccessRestriction() ||
                    isEmbedded ||
                    org.nonEmbedAccess === true)), setSelectedOrgId: setSelectedOrgId, refetch: refetch, selectedOrgId: selectedOrgId, isLegacy: isLegacy, isOrgPerUrlEnabled: sessionService.isOrgPerUrlEnabled(), areProfileAndHelpHidden: areProfileAndHelpHidden, getSubDomainIfOrgEnabled: navService.getSubDomainIfOrgEnabled, metricsService: metricsService })),
        React.createElement(GlobalAlert.Toast, { message: t('orgSwitcher.successToastMessage', {
                orgName: message,
            }), isVisible: isToastAlertVisible, onClose: () => setIsToastAlertVisible(false), autoHide: true })));
};
//# sourceMappingURL=org-menu.container.js.map